import { useEffect, useMemo, useState } from "react";
import { Ban, CalendarPlus, ChevronLeft, ChevronRight, Trash2, UserPlus, X } from "lucide-react";
import { api, cx, formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
};

const blankBlock = {
  type: "blackout",
  start_date: "",
  end_date: "",
  title: "",
  guest_name: "",
  guest_contact: "",
  notes: "",
};

/**
 * Per-service calendar modal — provider-only.
 *
 * Shows: bookings + blackouts + manual reservations on a month grid,
 *        with a side pane to add new entries and remove existing ones.
 */
export default function ServiceCalendarModal({ service, onClose }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const today = new Date();
    return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  });
  const [selectedISO, setSelectedISO] = useState(() => ymd(new Date()));
  const [draft, setDraft] = useState(blankBlock);

  const reload = async () => {
    setError(null);
    try {
      const { data } = await api.get(`/services/${service.id}/calendar`);
      setData(data);
    } catch (e) { setError(formatErr(e)); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [service?.id]);

  // Combine bookings + blackouts into a unified timeline with kind/colour.
  const items = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.bookings || []).map((b) => ({ ...b, kind: "booking" })),
      ...(data.blackouts || []),
    ];
  }, [data]);

  // ISO-date → array of items spanning that day
  const dayMap = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const start = parseISO(it.start_date);
      const end = parseISO(it.end_date) || start;
      if (!start) return;
      const cur = new Date(start.getTime());
      while (cur.getTime() <= end.getTime()) {
        const k = ymd(cur);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(it);
        cur.setUTCDate(cur.getUTCDate() + 1);
      }
    });
    return map;
  }, [items]);

  const grid = useMemo(() => {
    const first = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
    const dow = (first.getUTCDay() + 6) % 7;
    const start = new Date(first.getTime());
    start.setUTCDate(first.getUTCDate() - dow);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start.getTime());
      d.setUTCDate(start.getUTCDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString(undefined, {
    year: "numeric", month: "long", timeZone: "UTC",
  });
  const weekLabels = [
    t("calendar.dow_mon", "Mon"), t("calendar.dow_tue", "Tue"),
    t("calendar.dow_wed", "Wed"), t("calendar.dow_thu", "Thu"),
    t("calendar.dow_fri", "Fri"), t("calendar.dow_sat", "Sat"),
    t("calendar.dow_sun", "Sun"),
  ];

  const move = (delta) =>
    setCursor((c) => new Date(Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + delta, 1)));

  const startNew = (type) => {
    setDraft({
      ...blankBlock,
      type,
      start_date: selectedISO || ymd(new Date()),
      end_date: selectedISO || ymd(new Date()),
    });
    setFlash(null);
    setError(null);
  };

  const submitDraft = async (e) => {
    e?.preventDefault?.();
    if (!draft.start_date) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/services/${service.id}/blackouts`, {
        type: draft.type,
        start_date: draft.start_date,
        end_date: draft.end_date || draft.start_date,
        title: draft.title || null,
        guest_name: draft.type === "manual_reservation" ? (draft.guest_name || null) : null,
        guest_contact: draft.type === "manual_reservation" ? (draft.guest_contact || null) : null,
        notes: draft.notes || null,
      });
      setDraft(blankBlock);
      setFlash(t("service_calendar.saved", "Saved."));
      setTimeout(() => setFlash(null), 2500);
      await reload();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  const removeEntry = async (id) => {
    if (!window.confirm(t("service_calendar.remove_confirm", "Remove this entry from the calendar?"))) return;
    setBusy(true); setError(null);
    try {
      await api.delete(`/services/${service.id}/blackouts/${id}`);
      setFlash(t("service_calendar.removed", "Removed."));
      setTimeout(() => setFlash(null), 2500);
      await reload();
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  const selectedItems = (selectedISO && dayMap.get(selectedISO)) || [];
  const blackouts = (data?.blackouts || []).filter((b) => b.kind === "blackout");
  const manuals = (data?.blackouts || []).filter((b) => b.kind === "manual_reservation");

  const today = data?.today || ymd(new Date());

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm" data-testid="service-calendar-modal">
      <div className="w-full md:max-w-6xl max-h-[95svh] overflow-y-auto modal-panel">
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b hairline glass-solid z-10">
          <div className="min-w-0">
            <div className="eyebrow">{t("service_calendar.tab_calendar", "Calendar")}</div>
            <div className="font-serif-display text-xl truncate" data-testid="svc-cal-title">
              {t("service_calendar.title", "Calendar · {{title}}", { title: service?.title || "" })}
            </div>
            <div className="text-xs text-muted/70 mt-0.5">
              {t("service_calendar.subtitle", "Bookings, blocked dates and manual reservations for this service.")}
            </div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-bone shrink-0" aria-label="Close" data-testid="svc-cal-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 grid lg:grid-cols-[1fr,400px] gap-5">
          {/* Calendar grid */}
          <div className="luxe-card p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-serif-display text-2xl capitalize" data-testid="svc-cal-month">{monthLabel}</div>
              <div className="flex items-center gap-2">
                <button onClick={() => move(-1)} className="btn btn-ghost !px-2 !py-1.5" aria-label="Previous month" data-testid="svc-cal-prev">
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => {
                    const todayObj = parseISO(today) || new Date();
                    setCursor(new Date(Date.UTC(todayObj.getUTCFullYear(), todayObj.getUTCMonth(), 1)));
                    setSelectedISO(today);
                  }}
                  className="btn btn-ghost text-[12px]"
                  data-testid="svc-cal-today"
                >
                  {t("calendar.today", "Today")}
                </button>
                <button onClick={() => move(1)} className="btn btn-ghost !px-2 !py-1.5" aria-label="Next month" data-testid="svc-cal-next">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {weekLabels.map((d) => (
                <div key={d} className="label-tiny text-center">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {grid.map((d) => {
                const iso = ymd(d);
                const inMonth = d.getUTCMonth() === cursor.getUTCMonth();
                const isToday = iso === today;
                const isSelected = iso === selectedISO;
                const dayItems = dayMap.get(iso) || [];
                const hasBooking = dayItems.some((x) => x.kind === "booking");
                const hasBlackout = dayItems.some((x) => x.kind === "blackout");
                const hasManual = dayItems.some((x) => x.kind === "manual_reservation");
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelectedISO(iso)}
                    className={cx(
                      "relative aspect-square rounded-lg p-1.5 text-left text-xs transition-colors flex flex-col",
                      isSelected ? "bg-teal/15 border border-teal/60"
                        : isToday ? "border border-teal/30 bg-white/[0.02]"
                        : inMonth ? "hover:bg-white/[0.04] border border-transparent"
                        : "opacity-40 border border-transparent",
                    )}
                    data-testid={`svc-cal-cell-${iso}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={isToday ? "text-teal font-semibold" : "text-muted"}>{d.getUTCDate()}</span>
                      {dayItems.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-bone font-mono">
                          {dayItems.length}
                        </span>
                      )}
                    </div>
                    {dayItems.length > 0 && (
                      <div className="mt-auto flex flex-wrap gap-0.5">
                        {hasBooking && <span className="h-1.5 w-1.5 rounded-full bg-teal" />}
                        {hasBlackout && <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />}
                        {hasManual && <span className="h-1.5 w-1.5 rounded-full bg-champagne" />}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected day list */}
            <div className="mt-5 pt-4 border-t hairline">
              <div className="label-tiny mb-2">{selectedISO || "—"}</div>
              {selectedItems.length === 0 ? (
                <div className="text-sm text-muted">
                  {t("calendar.no_bookings_on_day", "No bookings on this day.")}
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedItems.map((it) => (
                    <DayRow key={`${it.kind}-${it.id}`} it={it} t={t} onRemove={removeEntry} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right pane — add + lists */}
          <div className="space-y-4" data-testid="svc-cal-actions">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => startNew("blackout")}
                className={cx(
                  "btn btn-outline text-[12px]",
                  draft.type === "blackout" && "border-rose-300/60 text-rose-200",
                )}
                data-testid="svc-cal-new-blackout"
              >
                <Ban size={14} /> {t("service_calendar.add_block", "Block dates")}
              </button>
              <button
                type="button"
                onClick={() => startNew("manual_reservation")}
                className={cx(
                  "btn btn-outline text-[12px]",
                  draft.type === "manual_reservation" && "border-champagne/60 text-champagne",
                )}
                data-testid="svc-cal-new-manual"
              >
                <UserPlus size={14} /> {t("service_calendar.add_manual", "Add manual reservation")}
              </button>
            </div>

            {(draft.start_date || draft.type) && (
              <form onSubmit={submitDraft} className="luxe-card p-4 space-y-3" data-testid="svc-cal-form">
                <div className="flex items-center gap-2">
                  {draft.type === "blackout" ? (
                    <span className="chip danger text-[10px]"><Ban size={10} /> {t("service_calendar.type_blackout", "Blackout (unavailable)")}</span>
                  ) : (
                    <span className="chip text-[10px]" style={{ borderColor: "rgba(212,175,55,0.5)", color: "rgb(220,200,140)" }}>
                      <CalendarPlus size={10} /> {t("service_calendar.type_manual_reservation", "Manual reservation")}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="label-tiny block mb-1.5">{t("service_calendar.start_date", "Start date")}</span>
                    <input
                      type="date"
                      required
                      value={draft.start_date}
                      onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                      className="input-bare"
                      data-testid="svc-cal-start"
                    />
                  </label>
                  <label className="block">
                    <span className="label-tiny block mb-1.5">{t("service_calendar.end_date", "End date")}</span>
                    <input
                      type="date"
                      required
                      value={draft.end_date}
                      min={draft.start_date}
                      onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                      className="input-bare"
                      data-testid="svc-cal-end"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="label-tiny block mb-1.5">{t("service_calendar.title_label", "Label")}</span>
                  <input
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    placeholder={
                      draft.type === "blackout"
                        ? t("service_calendar.title_ph_blackout", "Maintenance, off-season, private event…")
                        : t("service_calendar.title_ph_manual", "Guest name or short note")
                    }
                    className="input-bare"
                    maxLength={160}
                    data-testid="svc-cal-title-input"
                  />
                </label>

                {draft.type === "manual_reservation" && (
                  <div className="grid grid-cols-1 gap-3">
                    <label className="block">
                      <span className="label-tiny block mb-1.5">{t("service_calendar.guest_name", "Guest name (optional)")}</span>
                      <input
                        value={draft.guest_name}
                        onChange={(e) => setDraft({ ...draft, guest_name: e.target.value })}
                        className="input-bare"
                        maxLength={160}
                        data-testid="svc-cal-guest-name"
                      />
                    </label>
                    <label className="block">
                      <span className="label-tiny block mb-1.5">{t("service_calendar.guest_contact", "Guest contact (optional)")}</span>
                      <input
                        value={draft.guest_contact}
                        onChange={(e) => setDraft({ ...draft, guest_contact: e.target.value })}
                        placeholder="email · phone · IG handle"
                        className="input-bare"
                        maxLength={160}
                        data-testid="svc-cal-guest-contact"
                      />
                    </label>
                  </div>
                )}

                <label className="block">
                  <span className="label-tiny block mb-1.5">{t("service_calendar.notes", "Internal notes (only visible to you)")}</span>
                  <textarea
                    rows={2}
                    value={draft.notes}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    className="input-bare resize-y"
                    maxLength={1000}
                    data-testid="svc-cal-notes"
                  />
                </label>

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setDraft(blankBlock)}
                    className="btn btn-ghost text-[12px]"
                    data-testid="svc-cal-cancel"
                  >
                    {t("provider_dash.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={busy}
                    className={cx("btn btn-primary text-[12px]", busy && "opacity-60")}
                    data-testid="svc-cal-save"
                  >
                    {t("service_calendar.create", "Save")}
                  </button>
                </div>
              </form>
            )}

            {error && <div className="text-rose-300 text-sm" data-testid="svc-cal-error">{error}</div>}
            {flash && <div className="luxe-card p-3 text-sm text-teal border-teal/40" data-testid="svc-cal-flash">{flash}</div>}

            {/* Lists */}
            <ListBox
              title={t("service_calendar.list_blackouts", "Blocked dates")}
              chip="danger"
              icon={<Ban size={11} />}
              items={blackouts}
              onRemove={removeEntry}
              t={t}
              testid="svc-cal-list-blackouts"
            />
            <ListBox
              title={t("service_calendar.list_manual", "Manual reservations")}
              chip=""
              icon={<UserPlus size={11} />}
              items={manuals}
              onRemove={removeEntry}
              t={t}
              testid="svc-cal-list-manual"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function DayRow({ it, t, onRemove }) {
  const isBooking = it.kind === "booking";
  const isBlackout = it.kind === "blackout";
  return (
    <div className="luxe-card p-3" data-testid={`svc-cal-day-${it.kind}-${it.id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        {isBooking ? (
          <span className="chip success text-[10px]">{t("service_calendar.booking_chip", "Booking")}</span>
        ) : isBlackout ? (
          <span className="chip danger text-[10px]"><Ban size={10} /> {t("service_calendar.blocked_chip", "Blocked")}</span>
        ) : (
          <span className="chip text-[10px]" style={{ borderColor: "rgba(212,175,55,0.5)", color: "rgb(220,200,140)" }}>
            <UserPlus size={10} /> {t("service_calendar.manual_chip", "Manual")}
          </span>
        )}
        <span className="text-[11px] text-muted font-mono">
          {it.start_date}{it.end_date && it.end_date !== it.start_date ? ` → ${it.end_date}` : ""}
        </span>
      </div>
      <div className="text-sm">
        {isBooking
          ? `${it.user_name || it.user_email || ""}${it.guests ? ` · ${it.guests}` : ""}`
          : (it.title || it.guest_name || "—")}
      </div>
      {!isBooking && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => onRemove(it.id)}
            className="btn btn-ghost text-[11px] text-rose-300 !px-2 !py-1"
            data-testid={`svc-cal-day-remove-${it.id}`}
          >
            <Trash2 size={11} /> {t("service_calendar.remove", "Remove")}
          </button>
        </div>
      )}
    </div>
  );
}

function ListBox({ title, items, onRemove, t, icon, testid }) {
  return (
    <div className="luxe-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-tiny inline-flex items-center gap-1.5">{icon} {title}</span>
        <span className="text-[11px] text-muted font-mono">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted">{t("service_calendar.no_entries", "No entries yet.")}</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.id} className="flex items-start justify-between gap-2 text-sm">
              <div className="min-w-0">
                <div className="text-bone truncate">{it.title || it.guest_name || "—"}</div>
                <div className="text-[11px] text-muted font-mono">
                  {it.start_date}{it.end_date && it.end_date !== it.start_date ? ` → ${it.end_date}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRemove(it.id)}
                className="btn btn-ghost text-[11px] text-rose-300 !px-1.5 !py-1 shrink-0"
                aria-label="Remove"
                data-testid={`svc-cal-remove-${it.id}`}
              >
                <Trash2 size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
